import type { Exercise, Grade } from "@repo/hindi-shared";
import { letterById, wordById } from "@repo/hindi-shared";
import { useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";
import { RubricNumeral } from "../ui/RubricNumeral";
import { SpeakerIcon } from "../ui/SpeakerIcon";

import { gradeFromSelfMark } from "./grading";
import type { PaperDictationProps } from "./types";

/** Gap between the first and second dictation of an item, in ms. */
const SPEAK_GAP_MS = 4000;

/** Radius of the countdown ring, in SVG user units (viewBox 0 0 48 48). */
const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

type Phase = "intro" | "item" | "reveal";

/** What to speak and what to show for a single dictated item, letter or word. */
interface ItemContent {
  audioText: string;
  /** Devanagari glyph/word shown only in the reveal phase. */
  display: string;
  /** IAST transliteration shown beneath the reveal. */
  iast: string;
}

function resolveItem(itemId: string): ItemContent | null {
  const letter = letterById.get(itemId);
  if (letter !== undefined) {
    return {
      audioText: letter.audioText,
      display: letter.glyph,
      iast: letter.iast,
    };
  }
  const word = wordById.get(itemId);
  if (word !== undefined) {
    return {
      audioText: word.devanagari,
      display: word.devanagari,
      iast: word.iast,
    };
  }
  return null;
}

/** Inline single-color ink pen-nib glyph for the intro card. No gradients. */
function PenNibIcon() {
  return (
    <svg
      viewBox="0 0 48 48"
      width="56"
      height="56"
      aria-hidden="true"
      className="text-ink"
    >
      <path
        d="M24 4 L38 22 L24 44 L10 22 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <line
        x1="24"
        y1="10"
        x2="24"
        y2="34"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

interface CountdownRingProps {
  /** Fraction remaining, 1 → just started, 0 → elapsed. */
  fraction: number;
  reducedMotion: boolean;
  secondsRemaining: number;
}

/** Sindoor countdown ring (stroke-dashoffset); plain text under reduced motion. */
function CountdownRing({
  fraction,
  reducedMotion,
  secondsRemaining,
}: CountdownRingProps) {
  if (reducedMotion) {
    return (
      <p className="font-latin text-sm text-ink-soft" role="status">
        {secondsRemaining} <span lang="en">sec</span>
      </p>
    );
  }
  const offset = RING_CIRCUMFERENCE * fraction;
  return (
    <svg viewBox="0 0 48 48" width="48" height="48" aria-hidden="true">
      <circle
        cx="24"
        cy="24"
        r={RING_RADIUS}
        fill="none"
        stroke="var(--color-rule)"
        strokeWidth="3"
      />
      <circle
        cx="24"
        cy="24"
        r={RING_RADIUS}
        fill="none"
        stroke="var(--color-sindoor)"
        strokeWidth="3"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 24 24)"
        style={{ transition: "stroke-dashoffset 0.1s linear" }}
      />
    </svg>
  );
}

/**
 * PaperDictation — self-manages the WHOLE paper block: intro card, then
 * per-item dictation (speak twice with a {@link SPEAK_GAP_MS} gap while the
 * learner writes on real paper), then a reveal phase where every item is
 * shown oversized with a per-item self-mark, followed by a block summary
 * and a continue button that hands the last item's grade off to the
 * parent. The runner special-cases the "paper" `SessionBlock` kind to
 * mount this once with every exercise in the block, rather than one
 * exercise per mount like every other exercise component.
 *
 * Checkpoint resume: `alreadyCompleted` carries any items from `exercises`
 * the runner already has a grade for (a mid-block reload) — see it and
 * `SessionScreen`'s `alreadyCompletedPaperItems` for the full mechanism.
 */
export function PaperDictation({
  exercises,
  audio,
  onItemComplete,
  alreadyCompleted = [],
}: PaperDictationProps) {
  const prefersReducedMotion = useReducedMotion();

  // Checkpoint resume: skip dictation for any item the runner already has a
  // graded result for (a mid-block reload), and start right after the last
  // one instead of always restarting the whole block at item 0 — restarting
  // would re-dictate already-graded items and, when self-marked again, both
  // duplicate their SRS grade and desync `itemIndex` from the runner's
  // persisted `exerciseIndex` (see SessionScreen for the full mechanism).
  const completedGradeById = new Map(
    alreadyCompleted.map((c) => [c.itemId, c.grade]),
  );
  const resumeIndex = (() => {
    let i = 0;
    while (
      i < exercises.length &&
      completedGradeById.has(exercises[i]!.itemId)
    ) {
      i += 1;
    }
    return i;
  })();
  const resumedIntoReveal = resumeIndex >= exercises.length;
  // Any resume (even at item 0, e.g. a checkpoint saved before the first
  // mark) skips the intro card — the learner already saw it and clicked
  // "तैयार" once this session; only a genuinely fresh mount (nothing
  // completed yet) shows it.
  const isResuming = alreadyCompleted.length > 0;

  const [phase, setPhase] = useState<Phase>(() => {
    if (resumedIntoReveal) return "reveal";
    if (isResuming) return "item";
    return "intro";
  });
  const [itemIndex, setItemIndex] = useState(resumeIndex);
  const [elapsedMs, setElapsedMs] = useState(0);
  // Already-completed items are pre-marked from their real recorded grade —
  // display-only bookkeeping so the reveal phase shows the actual
  // correct/incorrect mark and doesn't re-offer a self-mark that would call
  // `onItemComplete` again for an item already recorded.
  const [marks, setMarks] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const [itemId, grade] of completedGradeById) {
      initial[itemId] = grade >= 2;
    }
    return initial;
  });
  // Self-marking two items back to back within one synchronous event
  // handler (the reveal buttons can chain like this — see the "double
  // onItemComplete" regression test) means the SECOND `handleMark` call
  // runs before React has flushed the first `setMarks` update, so a plain
  // read of `marks` state would miss the mark just made and, on the item
  // that actually completes the block, wrongly decide it ISN'T the last
  // one — firing `onItemComplete` immediately for it instead of deferring.
  // This ref always holds the latest marks synchronously, mirroring the
  // `runnerRef` fix in `SessionScreen`.
  const marksRef = useRef(marks);
  marksRef.current = marks;

  // The last item's mark is applied to local state immediately (so the
  // reveal summary paints), but its `onItemComplete` call is deferred until
  // the learner acknowledges the summary via the continue button below —
  // calling it synchronously would let the parent unmount this component
  // (advancing/finishing the runner) in the same batch as this state
  // update, discarding the summary before paint.
  const [pendingFinalResult, setPendingFinalResult] = useState<{
    grade: Grade;
    itemId: string;
  } | null>(null);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const current: Exercise | undefined = exercises[itemIndex];

  // Speak the current item once when it becomes active (mount or index
  // change), then again after SPEAK_GAP_MS unless replaced sooner.
  useEffect(() => {
    if (phase !== "item" || current === undefined) return;
    const content = resolveItem(current.itemId);
    if (content === null) return;

    setElapsedMs(0);
    void audio.speak(content.audioText);

    const gapTimer = window.setTimeout(() => {
      if (cancelledRef.current) return;
      void audio.speak(content.audioText);
    }, SPEAK_GAP_MS);

    // Drives the countdown ring; a no-op (plain text, not re-rendered by
    // this) under reduced motion, but harmless to keep running either way.
    const tickTimer = window.setInterval(() => {
      if (cancelledRef.current) return;
      setElapsedMs((prev) => Math.min(prev + 100, SPEAK_GAP_MS));
    }, 100);

    return () => {
      window.clearTimeout(gapTimer);
      window.clearInterval(tickTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the active item changes
  }, [phase, itemIndex]);

  function handleStart(): void {
    setPhase("item");
  }

  function handleReplay(): void {
    if (current === undefined) return;
    const content = resolveItem(current.itemId);
    if (content === null) return;
    void audio.speak(content.audioText);
  }

  function handleNextItem(): void {
    if (itemIndex + 1 < exercises.length) {
      setItemIndex((i) => i + 1);
    } else {
      setPhase("reveal");
    }
  }

  function handleMark(itemId: string, ok: boolean): void {
    // Read/write through the ref, not the `marks` state closure: two marks
    // can land back to back in one synchronous handler (see the "double
    // onItemComplete" regression test) with no re-render between them, and
    // a stale `marks` closure on the second call would both re-mark an
    // item already marked by the first call and misjudge which mark is
    // actually last in the block.
    if (marksRef.current[itemId] !== undefined) return; // already marked
    const nextMarks = { ...marksRef.current, [itemId]: ok };
    marksRef.current = nextMarks;
    setMarks(nextMarks);

    const grade = gradeFromSelfMark(ok);
    const isLastMark = exercises.every(
      (ex) => nextMarks[ex.itemId] !== undefined,
    );
    if (isLastMark) {
      // Hold this last call: firing `onItemComplete` synchronously here
      // would let the parent advance/finish the runner (and unmount this
      // component) in the same batch as the `setMarks` above, discarding
      // the "X/Y सही" summary before it ever paints. `handleContinue`
      // below fires it once the learner has actually seen the summary.
      setPendingFinalResult({ grade, itemId });
      return;
    }
    onItemComplete({ grade, itemId });
  }

  function handleContinue(): void {
    if (pendingFinalResult === null) return;
    const result = pendingFinalResult;
    setPendingFinalResult(null);
    onItemComplete(result);
  }

  if (phase === "intro") {
    return (
      <div className="flex flex-col items-center gap-8 text-center">
        <PenNibIcon />
        <p className="font-dev text-3xl text-ink">
          <DualLabel en="Take pen and paper" hi="क़लम-काग़ज़ लीजिए" />
        </p>
        <p className="font-latin text-base text-ink-soft">
          <span lang="en">Get ready to write on paper.</span>
        </p>
        <InkButton variant="sindoor" onClick={handleStart}>
          <DualLabel variant="inline" en="Ready" hi="तैयार" />
        </InkButton>
      </div>
    );
  }

  if (phase === "item" && current !== undefined) {
    const fraction = Math.max(0, 1 - elapsedMs / SPEAK_GAP_MS);
    const secondsRemaining = Math.ceil((SPEAK_GAP_MS - elapsedMs) / 1000);
    return (
      <div className="flex flex-col items-center gap-8 text-center">
        <p className="font-latin text-sm text-ink-soft uppercase">
          <span lang="en">
            {itemIndex + 1}/{exercises.length}
          </span>{" "}
          <span className="text-[0.85em] opacity-70">
            <RubricNumeral value={itemIndex + 1} />/
            <RubricNumeral value={exercises.length} />
          </span>
        </p>

        <CountdownRing
          fraction={fraction}
          reducedMotion={Boolean(prefersReducedMotion)}
          secondsRemaining={secondsRemaining}
        />

        <InkButton variant="ghost" onClick={handleReplay}>
          <SpeakerIcon className="mr-2" />
          <DualLabel variant="inline" en="Replay" hi="फिर सुनो" />
        </InkButton>

        <InkButton variant="sindoor" onClick={handleNextItem}>
          <DualLabel
            variant="inline"
            en="Did you write it all?"
            hi="सब लिख लिया?"
          />
        </InkButton>
      </div>
    );
  }

  // Reveal phase. In print (`print:` variants) the marks, buttons, and
  // summary disappear so the list prints as a clean answer sheet — just
  // the letterforms and their transliterations, black on the page.
  const allMarked = exercises.every((ex) => marks[ex.itemId] !== undefined);
  const correctCount = exercises.filter(
    (ex) => marks[ex.itemId] === true,
  ).length;

  return (
    <div className="flex flex-col items-center gap-10 text-center">
      {exercises.map((ex) => {
        const content = resolveItem(ex.itemId);
        if (content === null) return null;
        const mark = marks[ex.itemId];
        return (
          <div key={ex.itemId} className="flex flex-col items-center gap-3">
            <p
              lang="hi"
              className="font-display leading-none text-ink"
              style={{ fontSize: "clamp(120px, 16vw, 200px)" }}
            >
              {content.display}
            </p>
            <p className="font-latin text-xl text-ink-soft italic">
              {content.iast}
            </p>
            {mark === undefined ? (
              <div className="flex gap-4 print:hidden">
                <InkButton
                  variant="ghost"
                  onClick={() => handleMark(ex.itemId, false)}
                >
                  <DualLabel variant="inline" en="✗ Try again" hi="फिर से" />
                </InkButton>
                <InkButton
                  variant="sindoor"
                  onClick={() => handleMark(ex.itemId, true)}
                >
                  <DualLabel variant="inline" en="✓ Correct" hi="सही" />
                </InkButton>
              </div>
            ) : (
              <p className="font-dev text-sm text-ink-soft print:hidden">
                <span lang="en">{mark ? "✓ Correct" : "✗ Try again"}</span>
              </p>
            )}
          </div>
        );
      })}

      {allMarked && (
        <>
          <p className="font-latin text-lg text-ink print:hidden">
            <span lang="en">
              {correctCount}/{exercises.length} correct
            </span>{" "}
            <span className="text-[0.85em] opacity-70">
              <RubricNumeral value={correctCount} />/
              <RubricNumeral value={exercises.length} />
            </span>
          </p>
          {/* Only rendered when a call to the parent is actually pending —
              gates its advance until the learner has seen the summary
              above (see `handleMark`/`handleContinue`). If every item was
              already graded on resume, there's nothing left to hand off:
              the runner would already have advanced past this block. */}
          {pendingFinalResult !== null && (
            <InkButton
              variant="sindoor"
              className="print:hidden"
              onClick={handleContinue}
            >
              <DualLabel variant="inline" en="Continue →" hi="आगे →" />
            </InkButton>
          )}
        </>
      )}
    </div>
  );
}
